import type { TextWithSideImageComponent } from "../../types";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";
import { sanitizeRichTextHtml } from "../../utils/richTextHtml";

/**
 * Text with a side image. The entire text column sits on the LEFT (any number
 * of paragraphs — supplied as bodyHtml), and the image is on the right on
 * desktop/tablet, stacked below the text on mobile.
 */
export function TextWithSideImageRenderer({ component }: { component: TextWithSideImageComponent }) {
  const { imageSrc, imageAlt, firstParagraph, secondParagraph, governorName, governorHref = "#", bodyHtml } = component.content;
  const { src, options } = resolveImageValue(imageSrc);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);

  // Legacy fallback: link the governor's name inside the second paragraph.
  const linkedSecond = (() => {
    if (!governorName) return secondParagraph;
    const idx = secondParagraph.indexOf(governorName);
    if (idx === -1) return secondParagraph;
    return (
      <>
        {secondParagraph.slice(0, idx)}
        {" "}
        <RuntimeLink href={governorHref} className="font-semibold" style={{ color: "#005CA9" }}>{governorName}</RuntimeLink>
        {" "}
        {secondParagraph.slice(idx + governorName.length)}
      </>
    );
  })();

  return (
    <section className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <div className="min-w-0 flex-1 text-base xl:text-lg leading-7 text-[#0F2849]">
        {bodyHtml && bodyHtml.trim() ? (
          <div
            /* Listes à puces = module visuel de marque : carré ambre (le même
               repère que le filet des titres) et retrait suspendu PROPRE — le
               tiret tapé dans le contenu et l'indentation négative qui cassait
               l'alignement des lignes repliées sont abandonnés. */
            className="[&_p]:mt-4 [&>p:first-child]:mt-0 [&_a]:font-semibold [&_a]:text-[#005CA9] [&_a]:underline hover:[&_a]:no-underline [&_ol]:mt-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol>li]:mb-1.5 [&_ul]:mt-4 [&_ul]:list-none [&_ul]:pl-1 [&_ul]:space-y-2 [&_ul>li]:relative [&_ul>li]:pl-6 [&_ul>li]:before:absolute [&_ul>li]:before:left-0.5 [&_ul>li]:before:top-[0.55em] [&_ul>li]:before:h-2 [&_ul>li]:before:w-2 [&_ul>li]:before:rounded-[2px] [&_ul>li]:before:bg-[#FFA401] [&_ul>li]:before:content-['']"
            dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(bodyHtml) }}
          />
        ) : (
          <>
            <p>{firstParagraph}</p>
            <p className="mt-4">{linkedSecond}</p>
          </>
        )}
      </div>
      <div className="lg:shrink-0">
        <RuntimeImage
          src={src}
          alt={imageAlt}
          className={mergeClassNames(
            "mx-auto h-auto w-[240px] rounded-sm object-cover lg:mx-0 lg:w-[300px]",
            overrideProps.className
          )}
          style={mergeStyles(objectPositionStyle, overrideProps.style)}
          priority
        />
      </div>
    </section>
  );
}
